const ADMIN_BASE_PATH = '/admin';
const PARTNER_BASE_PATH = '/partner';
const MENTOR_BASE_PATH = '/mentor';
const SETTINGS_BASE_PATH = '/settings';

const createAdminRoute = (subpath: string): string => `${ADMIN_BASE_PATH}${subpath}`;
const createPartnerRoute = (subpath: string): string => `${PARTNER_BASE_PATH}${subpath}`;

const createMentorRoute = (subpath: string): string => `${MENTOR_BASE_PATH}${subpath}`;
const createSettingsRoute = (subpath: string): string => `${SETTINGS_BASE_PATH}${subpath}`;

const MEMBER_TOOLS_AND_TEMPLATES_ROUTES = {
  TOOLS_AND_TEMPLATES: '/tools-and-templates',

  TOOLS_AND_TEMPLATES_DOCUMENT_TEMPLATE_PATTERN: '/tools-and-templates/:slug',
  TOOLS_AND_TEMPLATES_DOCUMENT_TEMPLATE: (slug: string) => `/tools-and-templates/${slug}`,

  TOOLS_AND_TEMPLATES_EMAIL_TEMPLATE_PATTERN: '/tools-and-templates/emails/:slug',
  TOOLS_AND_TEMPLATES_EMAIL_TEMPLATE: (slug: string) => `/tools-and-templates/emails/${slug}`,
};

const ADMIN_PROTECTED_DOWNLOAD_ROUTES = {
  ADMIN_PROTECTED_DOWNLOADS: createAdminRoute('/protected-downloads'),

  ADMIN_PROTECTED_DOWNLOAD_NEW: createAdminRoute('/protected-downloads/new'),

  ADMIN_PROTECTED_DOWNLOAD_EDIT_PATTERN: createAdminRoute('/protected-downloads/:protectedDownloadId'),
  ADMIN_PROTECTED_DOWNLOAD_EDIT: (protectedDownloadId: string) =>
    createAdminRoute(`/protected-downloads/${protectedDownloadId}`),
};

const ADMIN_TOOLS_AND_TEMPLATES_ROUTES = {
  ADMIN_TOOLS_AND_TEMPLATES: createAdminRoute('/tools-and-templates'),

  ADMIN_TOOLS_AND_TEMPLATES_DOCUMENT_TEMPLATE_PATTERN: createAdminRoute('/tools-and-templates/:documentTemplateId'),
  ADMIN_TOOLS_AND_TEMPLATES_DOCUMENT_TEMPLATE: (documentTemplateName: string) =>
    createAdminRoute(`/tools-and-templates/${documentTemplateName}`),

  ADMIN_TOOLS_AND_TEMPLATES_EMAIL_TEMPLATE_PATTERN: createAdminRoute('/tools-and-templates/emails/:emailTemplateId'),
  ADMIN_TOOLS_AND_TEMPLATES_EMAIL_TEMPLATE: (emailTemplateId: string) =>
    createAdminRoute(`/tools-and-templates/emails/${emailTemplateId}`),

  ADMIN_ADD_DOCUMENT_TEMPLATE: createAdminRoute('/tools-and-templates/add'),

  ADMIN_ADD_EMAIL_TEMPLATE: createAdminRoute('/tools-and-templates/email/add'),
};

export const PUBLIC_ROUTES = {
  STORAGE_UPLOAD: '/api/storage/upload',
  HOME: '/',
  FOUNDER: '/founder',
  COMING_SOON: '/coming-soon',
  ABOUT_US: '/about-us',
  FACULTY: '/faculty',
  GET_IN_TOUCH: '/get-in-touch',
  LANDING_CALCULATORS: '/features/calculators',
  LANDING_SAFE_CALCULATOR: '/features/calculators/safe-note-calculator',
  LANDING_RUNWAY_CALCULATOR: '/features/calculators/startup-runway-calculator',
  LANDING_DILUTION_CALCULATOR: '/features/calculators/equity-dilution-calculator',
  // Landing (founders.* host) public course marketing pages. There is no /courses
  // index page — only /courses/:slug. LANDING_COURSES is the base path of those
  // pages. Distinct keys from MEMBER_ROUTES.COURSES — same path, different host —
  // so ROUTES' MEMBER_ROUTES spread can never silently shadow this one.
  LANDING_COURSES: '/courses',
  LANDING_COURSE_BY_SLUG_PATTERN: '/courses/:slug',
  LANDING_COURSE_BY_SLUG: (slug: string) => `/courses/${slug}`,
  // Auth Routes
  LOGIN: '/login',
  LOGOUT: '/logout',
  SIGNUP: '/signup',
  VERIFY: '/verify',
  FORGETPASSWORD: '/forgot-password',
  RESETPASSWORD: '/reset-password',

  // Goodie bag routes
  GOODIE_BAG: '/goodie-bag',
  PROTECTED_DOWNLOAD_GOODIES: '/goodie-bag/protected-downloads',
  DOCUMENT_TEMPLATE_GOODIES: '/goodie-bag/document-templates',
  EMAIL_TEMPLATE_GOODIES: '/goodie-bag/email-templates',
  SNACK_GOODIES: '/goodie-bag/snacks',
  DEAL_AND_DISCOUNT_GOODIES: '/goodie-bag/deals-and-discounts',

  // Public Documents
  PRIVACY_POLICY: '/legal/privacy-policy',
  COOKIE_POLICY: '/legal/cookie-policy',
  TERMS_OF_USE: '/legal/terms-of-use',

  // Singular calendar event page available to public directly
  // but not via the main navigation from main calendar page
  CALENDAR_EVENT_PATTERN: '/calendar/:slug',
  CALENDAR_EVENT: (slug: string) => `/calendar/${slug}`,

  CERTIFICATE_VERIFICATION_PATTERN: '/certificate/:certificateNumber',
  PROTECTED_DOWNLOAD_PATTERN: '/downloads/:slug',
  CERTIFICATE_VERIFICATION: (certificateNumber: string) => `/certificate/${certificateNumber}`,
  PROTECTED_DOWNLOAD: (slug: string) => `/downloads/${slug}`,
};

const MEMBER_ROUTES = {
  ONBOARDING: '/onboarding',
  DASHBOARD: '/dashboard',
  CHAT: '/chat',
  CHAT_PATTERN: '/chat/:conversationId',
  CHAT_CONVERSATION: (conversationId: string) => `/chat/${conversationId}`,
  PRICING: '/pricing',
  /** Personal email offer link: `/offer?plan=installments|one_time&offer=<token>`. Same checkout as /pricing. */
  OFFER: '/offer',
  CALENDAR: '/calendar',
  LIVE_EVENTS: '/live-events',
  ONE_ON_ONE_COACHING: '/one-on-one-coaching',
  ONE_ON_ONE_COACHING_ONBOARDING: '/one-on-one-coaching-onboarding',
  RECORDED_EVENTS: '/recorded-events',
  COURSES: '/courses',
  DISCOUNT_DEALS: '/discount-deals',

  DISCOUNT_DEAL_BY_SLUG_PATTERN: '/discount-deals/:slug',
  DISCOUNT_DEAL_BY_SLUG: (slug: string) => `/discount-deals/${slug}`,

  ...MEMBER_TOOLS_AND_TEMPLATES_ROUTES,

  LIVE_EVENT_BY_SLUG_PATTERN: '/live-events/:slug',
  LIVE_EVENT_BY_SLUG: (slug: string) => `/live-events/${slug}`,

  RECORDED_EVENT_BY_SLUG_PATTERN: '/recorded-events/:slug',
  RECORDED_EVENT_BY_SLUG: (slug: string) => `/recorded-events/${slug}`,

  COURSE_BY_SLUG_PATTERN: '/courses/:slug',
  COURSE_BY_SLUG: (slug: string) => `/courses/${slug}`,

  COURSE_PREVIEW_BY_SLUG_PATTERN: '/courses/:slug/preview',
  COURSE_PREVIEW_BY_SLUG: (slug: string) => `/courses/${slug}/preview`,

  MENTOR_BY_SLUG_PATTERN: '/mentor/:slug',
  MENTOR_BY_SLUG: (slug: string) => `/mentor/${slug}`,

  SCENARIOS: '/scenarios',

  SCENARIO_PATTERN: '/scenario/:scenarioId',
  SCENARIO: (scenarioId: string) => `/scenario/${scenarioId}`,

  COMMUNITY: '/community',

  COMMUNITY_MEMBERS: '/community/members',
  COMMUNITY_MEMBER_PATTERN: '/community/members/:memberKey',
  COMMUNITY_MEMBER: (memberKey: string) => `/community/members/${memberKey}`,

  COMMUNITY_POST_PATTERN: '/community/post/:id',
  COMMUNITY_POST: (id: string) => `/community/post/${id}`,

  BOOKING_SUCCESS: '/booking/success',
  BOOKING_CANCEL: '/booking/cancel',
};

export const ADMIN_ROUTES = {
  ADMIN_LOGIN: createAdminRoute('/login'),
  ADMIN_FORGOTPASSWORD: createAdminRoute('/forgot-password'),
  ADMIN_RESETPASSWORD: createAdminRoute('/reset-password'),
  ADMIN_DASHBOARD: createAdminRoute('/dashboard'),
  ADMIN_COURSES: createAdminRoute('/courses'),
  ADMIN_VIDEOS: createAdminRoute('/videos'),
  ADMIN_ADD_COURSE: createAdminRoute('/add-course'),
  ADMIN_MENTORS: createAdminRoute('/mentors'),
  ADMIN_ADD_MENTOR: createAdminRoute('/add-mentor'),
  ADMIN_EDIT_MENTOR_PATTERN: createAdminRoute('/mentors/:mentorId'),
  ADMIN_EDIT_MENTOR: (mentorId: string) => createAdminRoute(`/mentors/${mentorId}`),
  ADMIN_PARTNERS: createAdminRoute('/partners'),
  ADMIN_ADD_PARTNER: createAdminRoute('/add-partner'),
  ADMIN_PARTNER_DETAIL_PATTERN: createAdminRoute('/partners/:partnerId'),
  ADMIN_PARTNER_DETAIL: (partnerId: string) => createAdminRoute(`/partners/${partnerId}`),
  ADMIN_EXTRAS: createAdminRoute('/extras'),
  ADMIN_EVENTS: createAdminRoute('/events'),
  ADMIN_ADD_EVENT: createAdminRoute('/add-event'),

  ...ADMIN_PROTECTED_DOWNLOAD_ROUTES,
  ...ADMIN_TOOLS_AND_TEMPLATES_ROUTES,

  ADMIN_EDIT_COURSE_PATTERN: createAdminRoute('/courses/:courseId'),
  ADMIN_EDIT_COURSE: (courseId: string) => createAdminRoute(`/courses/${courseId}`),

  ADMIN_EDIT_EVENT_PATTERN: createAdminRoute('/edit-event/:id'),
  ADMIN_EDIT_EVENT: (id: string) => createAdminRoute(`/edit-event/${id}`),

  ADMIN_EDIT_VIDEO_PATTERN: createAdminRoute('/videos/:videoId'),
  ADMIN_EDIT_VIDEO: (id: string) => createAdminRoute(`/videos/${id}`),

  ADMIN_SCENARIOS: createAdminRoute('/scenarios'),
  ADMIN_ADD_SCENARIO: createAdminRoute('/add-scenario'),
  ADMIN_EDIT_SCENARIO_PATTERN: createAdminRoute('/scenarios/:scenarioId'),
  ADMIN_EDIT_SCENARIO: (scenarioId: string) => createAdminRoute(`/scenarios/${scenarioId}`),

  ADMIN_COMMUNITY: createAdminRoute('/community'),

  ADMIN_DISCOUNT_DEALS: createAdminRoute('/startup-deals'),
  ADMIN_DISCOUNT_DEALS_ORDER: createAdminRoute('/startup-deals/manage-order'),
  ADMIN_ADD_DISCOUNT_DEAL: createAdminRoute('/add-startup-deal'),
  ADMIN_EDIT_DISCOUNT_DEAL_PATTERN: createAdminRoute('/startup-deals/:id'),
  ADMIN_EDIT_DISCOUNT_DEAL: (id: string) => createAdminRoute(`/startup-deals/${id}`),

  ADMIN_FEATURE_FLAGS: createAdminRoute('/feature-flags'),
  ADMIN_ADD_FEATURE_FLAGS: createAdminRoute('/feature-flags/add'),
  ADMIN_EDIT_FEATURE_FLAGS_PATTERN: createAdminRoute('/feature-flags/:featureFlagId'),
  ADMIN_EDIT_FEATURE_FLAGS: (featureFlagId: string) => createAdminRoute(`/feature-flags/${featureFlagId}`),
  ADMIN_CERTIFICATES: createAdminRoute('/certificates'),
  ADMIN_MBA_OPERATIONS: createAdminRoute('/mba-operations'),
  ADMIN_PAYMENT_DISPUTES: createAdminRoute('/payment-disputes'),
  ADMIN_MEETING_REFUND_JOBS: createAdminRoute('/meeting-refund-jobs'),
  ADMIN_AI_INSTRUCTIONS: createAdminRoute('/ai-instructions'),
};

export const PARTNER_ROUTES = {
  PARTNER_LOGIN: createPartnerRoute('/login'),
  PARTNER_FORGOTPASSWORD: createPartnerRoute('/forgot-password'),
  PARTNER_RESETPASSWORD: createPartnerRoute('/reset-password'),
  PARTNER_DASHBOARD: createPartnerRoute('/dashboard'),
  PARTNER_MEMBERS: createPartnerRoute('/members'),
  PARTNER_COHORTS: createPartnerRoute('/cohorts'),
  PARTNER_COHORT_DETAIL_PATTERN: createPartnerRoute('/cohorts/:cohortId'),
  PARTNER_COHORT_DETAIL: (cohortId: string): string => createPartnerRoute(`/cohorts/${cohortId}`),
  PARTNER_LICENSE: createPartnerRoute('/license'),
  PARTNER_BILLING: createPartnerRoute('/billing'),
  PARTNER_ANALYTICS: createPartnerRoute('/analytics'),
  PARTNER_SETTINGS: createPartnerRoute('/settings'),
};

const MENTOR_ROUTES = {
  MENTOR_AVALABILITY_DAYS: createMentorRoute('/availability'),
  MENTOR_COACHING_DASHBOARD: createMentorRoute('/coaching'),
};

const SETTINGS_ROUTES = {
  PROFILE: createSettingsRoute('/profile'),
  NOTIFICATIONS: createSettingsRoute('/notifications'),
  SECURITY: createSettingsRoute('/security'),
  BILLING: createSettingsRoute('/billing'),
  INTEGRATIONS: createSettingsRoute('/integrations'),
  MANAGE_ACCOUNT: createSettingsRoute('/manage-account'),
  AVAILABILITY: createSettingsRoute('/availability'),
  AVAILABILITY_EDIT_PATTERN: createSettingsRoute('/availability/:scheduleId'),
  AVAILABILITY_EDIT: (scheduleId: string) => createSettingsRoute(`/availability/${scheduleId}`),
  MY_MBA: createSettingsRoute('/my-mba'),
};

export const ROUTES = {
  ...PUBLIC_ROUTES,
  ...MEMBER_ROUTES,
  ...ADMIN_ROUTES,
  ...PARTNER_ROUTES,
  ...MENTOR_ROUTES,
  ...SETTINGS_ROUTES,
};
